// @ts-nocheck

// App
import { useSelector } from 'react-redux';

// Hooks
import {
  useEdgePage,
  useLeftHandDots,
  useMaxPageNumber,
  useMaxVisiblePageNumber,
  useMiddlePageNumbers,
  useRightHandDots,
} from './hooks';

// Components
import PaginationItem from './PaginationItem';
import PaginationPrevNext from './PaginationPrevNext';

// Tracking

// Misc / utils
import IconArrowGrey from 'images/icons/arrow-filled-grey.min.svg';
import IconArrowGreen from 'images/icons/arrow-filled-green.min.svg';
import * as S from './styles';

const getArrowIcon = (isEdgePage) => {
  return isEdgePage ? IconArrowGrey : IconArrowGreen;
};

const PageNav = () => {
  // Redux
  const query = useSelector((state) => state.location.current.query);
  const currentPage = useSelector((state) => Number(state.location.current.query.page)) || 1;
  const searchSlug = useSelector((state) => state.filter.search.slug);
  const totalBuildings = useSelector((state) => state.listings.totalBuildings);
  const resourceId = useSelector((state) => state.area.area.resourceId);
  const screenWidth = useSelector((state) => state.app.device.screenWidth);

  // Custom hooks
  const { maxPageNumber } = useMaxPageNumber(totalBuildings, searchSlug);
  const { middlePages } = useMiddlePageNumbers(currentPage, maxPageNumber, screenWidth);
  const { maxVisiblePageNumber } = useMaxVisiblePageNumber(currentPage, maxPageNumber);
  const { showLeftHandDots } = useLeftHandDots(currentPage, maxPageNumber, screenWidth);
  const { showRightHandDots } = useRightHandDots(currentPage, maxPageNumber, screenWidth);
  const isEdgePageLeft = useEdgePage(currentPage - 1, maxPageNumber);
  const isEdgePageRight = useEdgePage(currentPage + 1, maxPageNumber);

  // Hide pagination if there's only one page
  if (maxPageNumber <= 1) {
    return null;
  }

  // Icons
  const LeftArrow = getArrowIcon(isEdgePageLeft);
  const RightArrow = getArrowIcon(isEdgePageRight);

  return (
    <S.PageNavContainer aria-label="Pagination">
      <S.PageNavOuterOrderedList>
        <S.PrevPageContainer>
          <PaginationPrevNext
            areaResourceId={resourceId}
            ariaCurrent={currentPage === 1 ? 'page' : null}
            ariaLabel={'Previous page'}
            isBold
            isDisabled={isEdgePageLeft}
            linkToPage={!isEdgePageLeft ? currentPage - 1 : undefined}
            linkType={isEdgePageLeft ? 'accent-light' : null}
            query={query}
            searchSlug={searchSlug}
          >
            <S.IconArrow src={LeftArrow} height="12px" width="12px" direction="left" alt="Previous page icon" />
            Prev
          </PaginationPrevNext>
        </S.PrevPageContainer>
        <S.FirstPageLi showDots={showLeftHandDots}>
          <PaginationItem
            areaResourceId={resourceId}
            ariaCurrent={currentPage === 1 ? 'page' : null}
            ariaLabel={'Link to page 1'}
            linkToPage={1}
            isActive={currentPage === 1}
            query={query}
            searchSlug={searchSlug}
          >
            1
          </PaginationItem>
        </S.FirstPageLi>
        {middlePages.map((page) => {
          return (
            <S.MiddlePagesLi key={`li-page-${page}-out-of-${maxPageNumber}`}>
              <PaginationItem
                areaResourceId={resourceId}
                ariaCurrent={currentPage === page ? 'page' : null}
                ariaLabel={`Link to page ${page}`}
                linkToPage={page}
                isActive={currentPage === page}
                query={query}
                searchSlug={searchSlug}
              >
                {page}
              </PaginationItem>
            </S.MiddlePagesLi>
          );
        })}
        <S.LastPageLi showDots={showRightHandDots}>
          <PaginationItem
            areaResourceId={resourceId}
            ariaCurrent={currentPage === maxVisiblePageNumber ? 'page' : null}
            ariaLabel={`Link to page ${maxVisiblePageNumber} ${
              maxVisiblePageNumber === maxPageNumber ? 'which is the last page' : ''
            }`}
            linkToPage={maxVisiblePageNumber}
            isActive={currentPage === maxVisiblePageNumber}
            query={query}
            searchSlug={searchSlug}
          >
            {maxVisiblePageNumber}
          </PaginationItem>
        </S.LastPageLi>
        <S.NextPageContainer>
          <PaginationPrevNext
            areaResourceId={resourceId}
            ariaCurrent={currentPage === 1 ? 'page' : null}
            ariaLabel={'Next page'}
            isBold
            isDisabled={isEdgePageRight}
            linkToPage={!isEdgePageRight ? currentPage + 1 : undefined}
            linkType={isEdgePageRight ? 'accent-light' : null}
            query={query}
            searchSlug={searchSlug}
          >
            Next
            <S.IconArrow src={RightArrow} height="12px" width="12px" direction="right" alt="Next page icon" />
          </PaginationPrevNext>
        </S.NextPageContainer>
      </S.PageNavOuterOrderedList>
    </S.PageNavContainer>
  );
};

export default PageNav;
